import React, { useState, useEffect, useCallback } from 'react';
import { 
  PhoneIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import Layout from './Layout';
import { callsAPI, getCallStatusColor, getCallStatusLabel, getCallTypeColor, getCallTypeLabel } from '../services/callApi';
import { Call, CallFilters, LeadForCall } from '../types/Call';
import CreateCallModal from './CreateCallModal';
import CallDetailModal from './CallDetailModal';
import LiveCallModal from './LiveCallModal';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';
import { useCallManagement } from '../contexts/CallManagementContext';

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
}

const CallsContent: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const { liveCall, startCall, endCall, cancelCall, rescheduleCall: contextRescheduleCall, setRefreshCalls } = useCallManagement();
  const [calls, setCalls] = useState<Call[]>([]);
  const [leads, setLeads] = useState<LeadForCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [rescheduleCallData, setRescheduleCallData] = useState<Call | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20
  });

  // Filters
  const [filters, setFilters] = useState<CallFilters>({
    status: '',
    callType: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 20,
    sortBy: 'scheduled_date',
    sortOrder: 'DESC'
  });

  // Fetch calls
  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const response = await callsAPI.getCalls(filters);
      
      console.log('🔍 Calls - Fetching response:', {
        totalCalls: response.data?.length || 0,
        firstCallLead: response.data?.[0]?.leadFirstName + ' ' + response.data?.[0]?.leadLastName,
        hasLeadData: !!response.data?.[0]?.leadFirstName
      });
      
      setCalls(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching calls:', error);
      setError('Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const response = await callsAPI.getAvailableLeads();
      setLeads(response);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, []);

  useEffect(() => {
    if (!branchLoading) {
      fetchCalls();
      fetchLeads();
    }
  }, [fetchCalls, fetchLeads, currentBranch, isViewingAllBranches, branchLoading]);

  // Register refresh function with context
  useEffect(() => {
    setRefreshCalls(() => fetchCalls);
  }, [fetchCalls, setRefreshCalls]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      callType: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      page: 1,
      limit: 10
    });
  };


  // Handle call creation
  const handleCreateCall = async (callData: any) => {
    try {
      const newCall = await callsAPI.createCall(callData);
      setCalls(prev => [newCall, ...prev]);
      setShowCreateModal(false);
      
      // If it's a live call, start it immediately
      if (callData.callType === 'live') {
        const startedCall = await callsAPI.startCall(newCall.id);
        startCall(startedCall);
      }
    } catch (error) {
      console.error('Error creating call:', error);
      setError('Failed to create call');
    }
  };

  // Handle call actions
  const handleStartCall = async (call: Call) => {
    try {
      const updatedCall = await callsAPI.startCall(call.id);
      startCall(updatedCall);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error starting call:', error);
      setError('Failed to start call');
    }
  };

  const handleEndCallWrapper = async (callId: number, summary: string, callPickedUp: boolean) => {
    try {
      await endCall(callId, summary, callPickedUp);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error ending call:', error);
      setError('Failed to end call');
    }
  };

  const handleCancelCallWrapper = async (callId: number, reason: string) => {
    try {
      await cancelCall(callId, reason);
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error cancelling call:', error);
      setError('Failed to cancel call');
    }
  };

  const handleRescheduleCallWrapper = async (callId: number, newDate: string, reason: string) => {
    try {
      // Check if this is a live call that wasn't picked up
      if (liveCall && liveCall.id === callId) {
        await callsAPI.handleNotPickedUp(callId, { reason, rescheduleDate: newDate });
        await contextRescheduleCall(callId, newDate, reason);
      } else {
        await callsAPI.rescheduleCall(callId, { newDate, reason });
      }
      fetchCalls(); // Refresh calls list
    } catch (error) {
      console.error('Error rescheduling call:', error);
      setError('Failed to reschedule call');
    }
  };


  // Handle call detail view
  const handleViewCall = (call: Call) => {
    setSelectedCall(call);
    setShowDetailModal(true);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  // Handle sorting
  const handleSort = (sortBy: string) => {
    const newSortOrder = filters.sortBy === sortBy && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setFilters(prev => ({ 
      ...prev, 
      sortBy, 
      sortOrder: newSortOrder,
      page: 1 // Reset to first page when sorting
    }));
  };

  // Handle call deletion
  const handleDeleteCall = async (callId: number) => {
    if (!window.confirm('Are you sure you want to delete this call? This action cannot be undone.')) {
      return;
    }

    try {
      await callsAPI.deleteCall(callId);
      setCalls(prev => prev.filter(call => call.id !== callId));
      setError(null);
    } catch (error) {
      console.error('Error deleting call:', error);
      setError('Failed to delete call');
    }
  };

  // Handle reschedule cancelled call (opens modal)
  const handleRescheduleCancelledCall = (call: Call) => {
    setRescheduleCallData(call);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
    setShowRescheduleModal(true);
  };

  // Wrapper for LiveCallModal reschedule (matches expected signature)
  const handleLiveCallReschedule = (callId: number, newDate: string, reason: string) => {
    handleRescheduleCallWrapper(callId, newDate, reason);
  };

  // Handle reschedule submission
  const handleRescheduleSubmit = async () => {
    if (!rescheduleCallData || !rescheduleDate || !rescheduleTime || !rescheduleReason.trim()) {
      setError('Please fill in all reschedule fields');
      return;
    }

    try {
      const newDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
      await callsAPI.rescheduleCancelledCall(
        rescheduleCallData.id,
        newDateTime.toISOString(),
        rescheduleReason
      );
      
      // Refresh the entire calls list from backend
      await fetchCalls();
      setShowRescheduleModal(false);
      setRescheduleCallData(null);
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleReason('');
      setError(null);
    } catch (error) {
      console.error('Error rescheduling call:', error);
      setError('Failed to reschedule call');
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <ClockIcon className="w-4 h-4" />;
      case 'in_progress':
        return <PhoneIcon className="w-4 h-4" />;
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4" />;
      case 'rescheduled':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  if (loading && calls.length === 0) {
    return (
      <Layout title="Calls">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Calls">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calls</h1>
            <p className="text-gray-600">Manage your call activities and schedules</p>
          </div>
          <PermissionGate module="calls" permission="create">
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={!!liveCall}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={liveCall ? 'End current call before starting a new one' : 'Create a new call'}
            >
              <PhoneIcon className="w-5 h-5 mr-2" />
              New Call
            </button>
          </PermissionGate>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search calls by lead name, company, phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Type</label>
              <select
                value={filters.callType}
                onChange={(e) => handleFilterChange('callType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="live">Live Call</option>
                <option value="recorded">Recorded Call</option>
                <option value="scheduled">Scheduled Call</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Calls List */}
        <div className="bg-white rounded-lg shadow border">
          {calls.length === 0 ? (
            <div className="text-center py-12">
              <PhoneIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No calls found</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first call</p>
              <PermissionGate module="calls" permission="create">
                <button
                  onClick={() => setShowCreateModal(true)}
                  disabled={!!liveCall}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={liveCall ? 'End current call before starting a new one' : 'Create a new call'}
                >
                  Create Call
                </button>
              </PermissionGate>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('scheduled_date')}
                    >
                      <div className="flex items-center">
                        Scheduled
                        {filters.sortBy === 'scheduled_date' && (
                          <span className="ml-1">
                            {filters.sortOrder === 'ASC' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('duration_seconds')}
                    >
                      <div className="flex items-center">
                        Duration
                        {filters.sortBy === 'duration_seconds' && (
                          <span className="ml-1">
                            {filters.sortOrder === 'ASC' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Motive/Agenda
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {calls.map((call) => (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {call.leadFirstName} {call.leadLastName}
                          </div>
                          <div className="text-sm text-gray-500">{call.leadCompany || call.leadEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {call.phoneNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallTypeColor(call.callType)}`}>
                          {getCallTypeLabel(call.callType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusColor(call.status)}`}>
                          {getStatusIcon(call.status)}
                          <span className="ml-1">{getCallStatusLabel(call.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(call.scheduledDate || call.actualStartTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {call.durationFormatted || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {call.motiveAgenda}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewCall(call)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          
                          {call.status === 'scheduled' && call.callType === 'live' && (
                            <PermissionGate module="calls" permission="edit">
                              <button
                                onClick={() => handleStartCall(call)}
                                className="text-green-600 hover:text-green-900"
                                title="Start Call"
                              >
                                <PhoneIcon className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                          )}
                          
                          <PermissionGate module="calls" permission="edit">
                            <button
                              onClick={() => handleViewCall(call)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Edit Call"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                          
                          {call.status === 'cancelled' && (
                            <PermissionGate module="calls" permission="create">
                              <button
                                onClick={() => handleRescheduleCancelledCall(call)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Reschedule Call"
                              >
                                <ClockIcon className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                          )}
                          
                          <PermissionGate module="calls" permission="delete">
                            <button
                              onClick={() => handleDeleteCall(call.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Call"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)} of {pagination.totalRecords} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateCallModal
          leads={leads}
          onClose={() => setShowCreateModal(false)}
          onCallCreated={handleCreateCall}
        />
      )}

      {showDetailModal && selectedCall && (
        <CallDetailModal
          call={selectedCall}
          onClose={() => setShowDetailModal(false)}
          onReschedule={handleLiveCallReschedule}
          onCancel={handleCancelCallWrapper}
        />
      )}

      {liveCall && (
        <LiveCallModal
          call={liveCall}
          onEndCall={handleEndCallWrapper}
          onReschedule={handleRescheduleCallWrapper}
          onCancelCall={handleCancelCallWrapper}
        />
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleCallData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reschedule Call</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead: {rescheduleCallData.leadFirstName} {rescheduleCallData.leadLastName}
                </label>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone: {rescheduleCallData.phoneNumber}
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date
                </label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Time
                </label>
                <input
                  type="time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reschedule Reason
                </label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  placeholder="Enter reason for rescheduling..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleCallData(null);
                    setRescheduleDate('');
                    setRescheduleTime('');
                    setRescheduleReason('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRescheduleSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Reschedule Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CallsContent;
